import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Hero from '@/components/Hero';
import FAQItem from '@/components/FAQItem';
import TourStructuredData from '@/components/tours/TourStructuredData';
import SEOMetadata from '@/components/common/SEOMetadata';

const PrivateTours = () => {
  const [guestCount, setGuestCount] = useState(8);
  
  const basePrice = 320;
  const additionalGuestPrice = 40;
  
  const calculateTotalPrice = () => {
    if (guestCount <= 8) {
      return basePrice;
    }
    
    const additionalGuests = guestCount - 8;
    return basePrice + (additionalGuests * additionalGuestPrice);
  };
  
  const faqs = [
    {
      question: "How far in advance should we book a private tour?",
      answer: "We recommend booking at least 2-3 weeks in advance for weekday tours and 4-6 weeks for weekend tours, especially during peak tourist seasons (spring through fall)."
    },
    {
      question: "Can we customize our private tour?",
      answer: "Absolutely! We can tailor our historical cat tales to your group's specific interests. Whether you want more focus on working cats, famous feline residents, or cat-loving historical figures, we'll customize the tour to match your interests. Want to include more modern cat stories? Just let us know! You're also welcome to bring your cats along, making it truly your own unique experience."
    },
    {
      question: "Do you offer tours in languages other than English?",
      answer: "Currently, our tours are only available in English, but we're working on expanding our language offerings. Please contact us if you have specific language requirements, as we may be able to accommodate with advance notice."
    },
    {
      question: "Can we bring our cats on the tour?",
      answer: "Yes! We welcome well-behaved cats on our private tours. We recommend secure harnesses or carriers for their safety. A private tour is the perfect opportunity for your feline friends to experience the historical paths where their ancestors once roamed. Just let us know in advance so we can plan accordingly!"
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      <SEOMetadata
        title="Private Cat Tours NYC - Custom Historical Walking Tours | Cats About Town"
        description="Book a private, custom cat history tour for your group or special occasion. Explore NYC's feline past with a personalized experience starting at $320!"
        keywords={[
          "Private cat tour NYC",
          "custom NYC tour",
          "group cat walking tour",
          "NYC cat history private",
          "corporate team building NYC",
          "private walking tour",
          "custom cat tour",
          "group tour NYC",
          "private historical tour",
          "NYC private guide"
        ]}
        canonicalUrl="https://catsabouttowntours.com/tours/private-tours"
        ogImage="/lovable-uploads/258ca9bd-d5f4-4734-8b50-73e073d9313c.png"
        ogImageAlt="Private Cat Tours NYC - Custom historical walking tours for groups"
      />
      
      <Helmet>
        {/* Breadcrumb structured data for SEO */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://catsabouttowntours.com/" },
              { "@type": "ListItem", "position": 2, "name": "Tours", "item": "https://catsabouttowntours.com/tours" },
              { "@type": "ListItem", "position": 3, "name": "Private Cat Tours", "item": "https://catsabouttowntours.com/tours/private-tours" }
            ]
          })}
        </script>
      </Helmet>
      
      <TourStructuredData tour={{
        name: 'Private Cat Tours NYC',
        description: "Book a private, custom cat history tour for your group or special occasion. Explore NYC's feline past with a personalized experience!",
        image: '/lovable-uploads/258ca9bd-d5f4-4734-8b50-73e073d9313c.png',
        url: 'https://catsabouttowntours.com/tours/private-tours',
        price: '320',
        location: 'New York City',
        duration: 'PT2H',
        highlights: [
          'Customizable neighborhoods',
          'Private group experience',
          'Corporate team building',
          'Special event tours',
          'Cat-friendly tours'
        ]
      }} />
      
      {/* Hero Section */}
      <Hero 
        title="Private Historical Cat Tours & Special Events" 
        subtitle="Create a customized historical cat tale experience for your group"
        backgroundImage="/lovable-uploads/bd21f9ef-b1f2-4bd5-b145-a694d400d436.png"
        height="medium"
        overlay={false}
        backgroundAlt="Group enjoying private NYC cat tour"
      />
      
      {/* Tour Overview */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="vintage-card mb-12">
              <h2 className="text-2xl font-playfair font-bold mb-6">Private Walking Tour Experience</h2>
              <p className="text-lg mb-6">
                Discover the hidden feline history of New York City with a private tour designed exclusively for your group. Our expert guides provide a personalized experience through historic neighborhoods as you explore the fascinating cat tales of Old New York.
              </p>
              <p className="mb-6">
                Explore New York City's fascinating historical feline tales with a private walking tour designed exclusively for your group. Our expert guides will take you through historic neighborhoods as you discover the hidden cat stories of Old New York—primarily from the 1800s and early 1900s—at your own pace.
              </p>
              <p className="mb-6">
                Our tours focus on historical stories rather than cat-spotting, though we're always delighted when a modern-day feline makes an appearance along our route! Feel free to bring your own cat companions to join the historical adventure (just ensure they're comfortable with city walks and secure in a harness or carrier).
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
              <div className="vintage-card h-full">
                <h2 className="text-2xl font-playfair font-bold mb-6">Tour Details & Pricing</h2>
                <p className="mb-6">
                  Our private tours are priced at $320 minimum (equivalent to 8 people at $40 per person), even for smaller groups. Additional guests beyond 8 people are $40 per person. Tours last approximately 2 hours and are perfect for families, corporate teams, birthday celebrations, or special events.
                </p>
                
                <div className="bg-neutral-tan p-4 rounded-lg mb-6">
                  <h3 className="font-playfair font-bold mb-3">Available Tour Neighborhoods:</h3>
                  <ul className="paw-list">
                    <li>Brooklyn Heights</li>
                    <li>Financial District</li>
                    <li>Lower East Side</li>
                  </ul>
                  <p className="mt-4 text-sm text-text/80">
                    Custom neighborhoods may be available upon request.
                  </p>
                  <p className="mt-4 text-sm text-text/80">
                    Each neighborhood features its own unique collection of historical cat tales. While we can't guarantee live cat sightings, the stories will bring the neighborhood's feline past to life!
                  </p>
                </div>
              </div>
              
              <div className="vintage-card h-full">
                <h2 className="text-2xl font-playfair font-bold mb-6">Scheduling Your Private Tour</h2>
                <p className="mb-6">
                  We offer the greatest availability on weekends. Weekday tours require at least 2 weeks advance notice to ensure we can secure your preferred guide. Evening tours are available upon request, subject to guide availability.
                </p>
                <p>
                  Contact us with your preferred date, time, neighborhood (Brooklyn Heights, Financial District, or Lower East Side), and group size. We'll get back to you within 48 hours to confirm details and finalize your booking.
                </p>
              </div>
            </div>
            
            {/* Price Calculator */}
            <div className="premium-card mb-12">
              <h2 className="text-2xl font-playfair font-bold mb-6 text-center">Price Calculator</h2>
              <div className="flex flex-col sm:flex-row items-center justify-between mb-8">
                <label className="font-medium mb-2 sm:mb-0">Number of Guests:</label>
                <input 
                  type="number"
                  min="1"
                  max="30"
                  value={guestCount}
                  onChange={(e) => setGuestCount(parseInt(e.target.value) || 1)}
                  className="border border-primary/20 rounded p-2 w-full sm:w-40 text-center"
                />
              </div>
              
              <div className="bg-neutral-tan p-4 rounded-lg mb-6">
                <div className="flex justify-between mb-3">
                  <span>Base Tour Price (minimum, up to 8 guests):</span>
                  <span className="font-semibold">${basePrice}</span>
                </div>
                <div className="flex justify-between mb-3">
                  <span>Additional Guests ({Math.max(0, guestCount - 8)} x ${additionalGuestPrice}):</span>
                  <span className="font-semibold">${Math.max(0, guestCount - 8) * additionalGuestPrice}</span>
                </div>
                <div className="border-t border-primary/20 pt-3 flex justify-between font-bold">
                  <span>Total:</span>
                  <span>${calculateTotalPrice()}</span>
                </div>
              </div>
              
              <div className="text-center">
                <Link to="/contact" className="btn-gold">
                  Ready to book your event?
                </Link>
              </div>
            </div>
            
            {/* Gallery */}
            <div className="mb-12">
              <h2 className="section-title text-center after:left-0 after:right-0 after:mx-auto mb-8">
                Perfect For Special Events
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="rounded-lg overflow-hidden aspect-square relative group">
                  <img src="/placeholder.svg" alt="Corporate team event" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-white font-medium">Corporate Team Building</p>
                  </div>
                </div>
                <div className="rounded-lg overflow-hidden aspect-square relative group">
                  <img src="/placeholder.svg" alt="Birthday celebration" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-white font-medium">Birthday Celebrations</p>
                  </div>
                </div>
                <div className="rounded-lg overflow-hidden aspect-square relative group">
                  <img src="/placeholder.svg" alt="Cat Enthusiast Groups" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-white font-medium">Cat Enthusiast Groups - Bring your feline friends along for a unique outing!</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-16 bg-neutral-tan">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="section-title-centered">Frequently Asked Questions</h2>
            <div className="space-y-4 mt-8">
              {faqs.map((faq, index) => (
                <FAQItem 
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                />
              ))}
            </div>
            
            <div className="text-center mt-10">
              <p className="mb-6">
                Want to create a unique historical cat tour experience for your group? Let's make it happen! Cats are welcome to join the adventure!
              </p>
              <Link to="/contact" className="btn-outline-gold">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivateTours;
